// dayone page bundle -> dist/js/pages/dayone.min.js
//
// Plain-ES port of the v3 dayone interactions (originally a React/DCLogic
// component: people-equation/dc/dayone.logic.js on feat/v3-design-pages, as the
// running :8015 baseline served it). No framework: nodes are re-selected via
// data-* hooks. Props hardcoded to the v3 render defaults (mapGrain 7,
// flowSpeed 1, showGraticule true).
//
// The signature hero is a D3 world map drawn to <canvas>: Natural-Earth country
// outlines (world-atlas 110m) projected with d3.geoNaturalEarth1().fitExtent,
// filled to an offscreen buffer via d3.geoPath and sampled on a pixel grid so the
// world reads as a field of thin PE dots; four origins send flow corridors toward
// India. d3 + topojson-client are loaded as global scripts (bundled locally in
// assets/vendor/, exact v3 versions); the countries-110m.json URL is read from
// the canvas data-atlas attribute and fetched at runtime (no CDN).
//
// Also ported: the four-stage "Building the centre" rail (both the desktop grid
// rail and the mobile column rail are SSR'd; applySteps styles both branches with
// each branch's own width rules), the "How we engage" way-card hover, the
// leadership profile drawer (reading #pe-dayone-people), the clientele marquee
// hover-pause, the insights card hover, and the closing CTA reveal. Shared nav
// interactions (scroll auto-hide, dropdowns, mobile menu) live in chrome.js.

(function () {
  "use strict";

  var root = document.querySelector(".pe-dayone");
  if (!root) return;

  var $ = function (sel, ctx) { return (ctx || document).querySelector(sel); };
  var $$ = function (sel, ctx) { return Array.prototype.slice.call((ctx || document).querySelectorAll(sel)); };
  var reduced = window.matchMedia && window.matchMedia("(prefers-reduced-motion: reduce)").matches;

  /* ============================ hero world map ========================== */
  // real geometry, abstracted: Natural Earth country outlines projected, filled
  // to an offscreen buffer, then sampled on a pixel grid; four origins send thin
  // corridors towards India, and India settles brighter with every arrival.
  var Map = (function () {
    var cv = $("[data-cv]", root);
    if (!cv) return null;

    var GRAIN = 7, BEAT = 1, GRATICULE = true; // v3 prop defaults
    // the corridor the story lives in — cropping the empty Pacific and deep south
    var CORRIDOR = { type: "Polygon", coordinates: [[[-130, -32], [143, -32], [143, 76], [-130, 76], [-130, -32]]] };
    // each origin takes its own corridor to India; waypoints are geographic
    var ORIGINS = [
      { k: "us", lon: -87.6, lat: 41.9, tag: "US", dl: 0.00, via: [[-38, 26], [26, 8]], lx: 0, ly: -30 },
      { k: "se", lon: 18.1, lat: 59.3, tag: "SE", dl: 0.26, via: [[36, 69], [73, 49]], lx: 4, ly: -32 },
      { k: "de", lon: 13.4, lat: 52.5, tag: "DE", dl: 0.52, via: [[35, 50], [64, 35]], lx: 36, ly: -6 },
      { k: "fr", lon: 2.35, lat: 48.9, tag: "FR", dl: 0.78, via: [[23, 33], [53, 23]], lx: -34, ly: 22 }
    ];
    var INDIA = { lon: 73.85, lat: 18.52 }; // Pune

    var ctx = null, W = 0, H = 0, box = null, projFn = null;
    var world = null, landPts = null, dotR = 1, dest = null, flows = null, rules = null;
    var t = 0, t0 = 0, lastFrame = 0, raf = null, watch = null;
    var mtx = 0, mty = 0, px = 0, py = 0, cur = null;
    var arrivals = [], landed = 0, atlasStarted = false;

    function grain() { return Math.max(5, Math.min(11, GRAIN)); }
    function beat() { return Math.max(0.5, Math.min(2, BEAT)); }

    function layoutCanvas() {
      var vw = window.innerWidth;
      var narrow = vw < 900;
      cv.style.width = narrow ? "100%" : (vw < 1200 ? "52%" : "56%");
      // Full opacity everywhere. The .42 used to dim the narrow canvas because it
      // overlapped the headline; it now sits stacked below the copy (see the hero
      // stacking rule in dayone.scss), so it can read at full strength.
      cv.style.opacity = "1";
      cv.style.maskImage = narrow
        ? "linear-gradient(to bottom,transparent 0%,rgba(0,0,0,1) 46%)"
        : "linear-gradient(to right,transparent 0%,rgba(0,0,0,1) 24%,rgba(0,0,0,1) 94%,transparent 100%)";
      cv.style.webkitMaskImage = cv.style.maskImage;
    }

    function sizeUnit() {
      if (!ctx) return;
      var r = cv.getBoundingClientRect();
      W = Math.max(1, r.width);
      H = Math.max(1, r.height);
      var dpr = Math.min(window.devicePixelRatio || 1, 2);
      cv.width = Math.round(W * dpr);
      cv.height = Math.round(H * dpr);
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
      buildMap();
    }

    function loadAtlas() {
      if (atlasStarted) return;
      atlasStarted = true;
      var url = cv.getAttribute("data-atlas") || "";
      var go = function () {
        if (!window.d3 || !window.topojson || !url) { window.setTimeout(go, 120); return; }
        fetch(url)
          .then(function (r) { return r.json(); })
          .then(function (topo) {
            var fc = window.topojson.feature(topo, topo.objects.countries);
            // Antarctica adds a heavy band along the bottom edge and nothing to the story
            world = { type: "FeatureCollection", features: fc.features.filter(function (f) { return String(f.id) !== "010"; }) };
            t0 = performance.now();
            buildMap();
          })
          .catch(function () {});
      };
      go();
    }

    function buildMap() {
      if (!world || !window.d3 || !W) return;
      var d3 = window.d3;
      var bw = Math.round(W * 0.94);
      var bh = Math.round(Math.min(H * 0.9, bw * 0.62));
      box = { x: Math.round((W - bw) / 2), y: Math.round((H - bh) / 2), w: bw, h: bh };

      var proj = d3.geoNaturalEarth1().fitExtent([[0, 0], [bw, bh]], CORRIDOR);
      projFn = proj;

      // fill the countries to an offscreen buffer, then read the pixels back as dots
      var off = document.createElement("canvas");
      off.width = bw;
      off.height = bh;
      var oc = off.getContext("2d");
      oc.fillStyle = "#000";
      oc.beginPath();
      d3.geoPath(proj, oc)(world);
      oc.fill();

      var pix = oc.getImageData(0, 0, bw, bh).data;
      var stepPx = Math.round(grain());
      landPts = [];
      for (var y = 2; y < bh; y += stepPx) {
        for (var x = 2; x < bw; x += stepPx) {
          if (pix[(y * bw + x) * 4 + 3] < 130) continue;
          landPts.push({ x: box.x + x, y: box.y + y, u: x / bw });
        }
      }
      dotR = Math.max(1, Math.min(2, stepPx * 0.21));

      var at = function (lon, lat) {
        var p = proj([lon, lat]);
        return { x: box.x + p[0], y: box.y + p[1] };
      };
      dest = at(INDIA.lon, INDIA.lat);
      flows = ORIGINS.map(function (o) {
        var a = at(o.lon, o.lat);
        return {
          tag: o.tag, dl: o.dl, lx: o.lx, ly: o.ly,
          a: a, b: dest,
          c1: at(o.via[0][0], o.via[0][1]),
          c2: at(o.via[1][0], o.via[1][1]),
          cars: [{ u: -o.dl }, { u: -o.dl - 0.52 }]
        };
      });
      // the two reference parallels, projected
      rules = [0, 23.5].map(function (lat) {
        var pts = [];
        for (var lon = -134; lon <= 150; lon += 4) pts.push(at(lon, lat));
        return pts;
      });
    }

    // one point along a lane (cubic Bezier)
    function lane(f, o) {
      var m = 1 - f;
      var w0 = m * m * m, w1 = 3 * m * m * f, w2 = 3 * m * f * f, w3 = f * f * f;
      return {
        x: w0 * o.a.x + w1 * o.c1.x + w2 * o.c2.x + w3 * o.b.x,
        y: w0 * o.a.y + w1 * o.c1.y + w2 * o.c2.y + w3 * o.b.y
      };
    }
    function square(c, x, y, r) { c.beginPath(); c.rect(x - r, y - r, r * 2, r * 2); c.closePath(); }
    function diamond(c, x, y, r) { c.beginPath(); c.moveTo(x, y - r); c.lineTo(x + r, y); c.lineTo(x, y + r); c.lineTo(x - r, y); c.closePath(); }
    function dot(c, x, y, r) { c.beginPath(); c.arc(x, y, r, 0, Math.PI * 2); c.closePath(); }

    function start() {
      stop();
      lastFrame = 0;
      var tick = function () {
        frame();
        if (!reduced) raf = requestAnimationFrame(tick);
      };
      tick();
      if (!reduced) {
        watch = window.setInterval(function () {
          if (performance.now() - (lastFrame || 0) > 320) frame();
        }, 120);
      }
    }
    function stop() {
      if (raf) cancelAnimationFrame(raf);
      if (watch) window.clearInterval(watch);
      raf = null; watch = null;
    }

    // the map holds still; only the four paths, their markers and India move
    function frame() {
      var c = cv.getContext("2d");
      ctx = c;
      if (!c) return;
      if (world && !landPts) buildMap();
      if (!landPts || !flows) return;

      var now = performance.now();
      var dt = Math.min(64, lastFrame ? now - lastFrame : 16);
      lastFrame = now;
      var k = Math.min(1, dt / 16);
      t += reduced ? 0 : dt / 1000;
      var entry = reduced ? 1 : Math.min(1, (now - t0) / 2400);
      var soft = function (v) { return v <= 0 ? 0 : v >= 1 ? 1 : 1 - Math.pow(1 - v, 3); };

      px += ((mtx * 10) - px) * 0.045 * k;
      py += ((mty * 7) - py) * 0.045 * k;

      c.clearRect(0, 0, W, H);
      c.save();
      c.translate(px, py);
      c.lineCap = "round";
      c.lineJoin = "round";

      if (GRATICULE && rules) {
        c.lineWidth = 1;
        c.strokeStyle = "rgba(23,114,111,.10)";
        rules.forEach(function (pts, i) {
          c.setLineDash(i ? [2, 9] : []);
          c.beginPath();
          pts.forEach(function (p, j) { if (j === 0) c.moveTo(p.x, p.y); else c.lineTo(p.x, p.y); });
          c.stroke();
        });
        c.setLineDash([]);
      }

      // land, resolving left to right on entry
      for (var i = 0; i < landPts.length; i++) {
        var p = landPts[i];
        var a = soft((entry * 1.35 - p.u * 0.85) / 0.5);
        if (a <= 0.01) continue;
        c.fillStyle = "rgba(23,114,111," + (0.27 * a).toFixed(3) + ")";
        dot(c, p.x, p.y, dotR);
        c.fill();
      }

      // the four paths
      for (var fi = 0; fi < flows.length; fi++) {
        var f = flows[fi];
        var draw = soft((entry - f.dl * 0.3) / 0.62);
        if (draw <= 0.01) continue;

        c.lineWidth = 1.6;
        c.strokeStyle = "rgba(23,114,111," + (0.44 * draw).toFixed(3) + ")";
        c.setLineDash([3, 8]);
        c.beginPath();
        for (var s = 0; s <= 34; s++) {
          var q = lane((s / 34) * draw, f);
          if (s === 0) c.moveTo(q.x, q.y); else c.lineTo(q.x, q.y);
        }
        c.stroke();
        c.setLineDash([]);

        // origin: a small hollow marker, a leader out to clear space, then the tick
        var oa = soft((entry - f.dl * 0.3) / 0.4);
        c.lineWidth = 1.3;
        c.strokeStyle = "rgba(42,134,140," + (0.75 * oa).toFixed(3) + ")";
        c.beginPath(); c.arc(f.a.x, f.a.y, 3.2, 0, Math.PI * 2); c.stroke();
        c.fillStyle = "rgba(42,134,140," + (0.55 * oa).toFixed(3) + ")";
        dot(c, f.a.x, f.a.y, 1.5); c.fill();

        var L = Math.hypot(f.lx, f.ly) || 1;
        var sx = f.a.x + (f.lx / L) * 4.8, sy = f.a.y + (f.ly / L) * 4.8;
        var ex = f.a.x + f.lx, ey = f.a.y + f.ly;
        c.lineWidth = 1;
        c.strokeStyle = "rgba(23,114,111," + (0.28 * oa).toFixed(3) + ")";
        c.beginPath(); c.moveTo(sx, sy); c.lineTo(ex, ey); c.stroke();

        c.font = '500 10px "IBM Plex Mono", monospace';
        c.textAlign = f.lx > 8 ? "left" : (f.lx < -8 ? "right" : "center");
        c.textBaseline = f.ly < -8 ? "bottom" : (f.ly > 8 ? "top" : "middle");
        c.fillStyle = "rgba(23,114,111," + (0.6 * oa).toFixed(3) + ")";
        c.fillText(f.tag, ex + (f.lx > 8 ? 3 : f.lx < -8 ? -3 : 0), ey + (f.ly < -8 ? -2 : f.ly > 8 ? 2 : 0));
        c.textBaseline = "alphabetic";

        if (draw < 0.999) continue;

        // capability moving: small squares, every other one an orange diamond
        for (var ci = 0; ci < f.cars.length; ci++) {
          var car = f.cars[ci];
          if (!reduced) car.u += 0.000105 * beat() * dt;
          if (car.u >= 1) {
            car.u -= 1.04 + (ci * 0.06);
            arrivals.push({ t: now });
            landed = Math.min(14, landed + 1);
            continue;
          }
          if (car.u <= 0) continue;
          var u = car.u;
          var qq = lane(u, f);

          c.lineWidth = 1.4;
          c.strokeStyle = "rgba(242,103,58,.24)";
          c.beginPath();
          for (var ss = 0; ss <= 8; ss++) {
            var uu = Math.max(0, u - 0.11 * (1 - ss / 8));
            var pp = lane(uu, f);
            if (ss === 0) c.moveTo(pp.x, pp.y); else c.lineTo(pp.x, pp.y);
          }
          c.stroke();

          var near = Math.max(0, (u - 0.72) / 0.28);
          if ((fi + ci) % 2 === 0) {
            c.fillStyle = "rgba(23,114,111," + (0.72 + 0.28 * near).toFixed(3) + ")";
            square(c, qq.x, qq.y, 2.6);
            c.fill();
          } else {
            c.fillStyle = "rgba(242,103,58," + (0.68 + 0.32 * near).toFixed(3) + ")";
            diamond(c, qq.x, qq.y, 3.4);
            c.fill();
          }
        }
      }

      // India: brighter as capability lands, then holding steady
      var d = dest;
      var warm = landed / 14;
      var ia = soft((entry - 0.2) / 0.6);
      if (ia > 0.01) {
        var pulse = 0.5 + 0.5 * Math.sin(t * 1.15);
        var g = c.createRadialGradient(d.x, d.y, 0, d.x, d.y, 34 + 16 * warm);
        g.addColorStop(0, "rgba(242,103,58," + (0.16 + 0.14 * warm).toFixed(3) + ")");
        g.addColorStop(1, "rgba(242,103,58,0)");
        c.fillStyle = g;
        dot(c, d.x, d.y, 34 + 16 * warm); c.fill();

        c.lineWidth = 1.2;
        c.strokeStyle = "rgba(242,103,58," + ((0.34 + 0.3 * warm) * ia).toFixed(3) + ")";
        c.beginPath(); c.arc(d.x, d.y, 9 + 2.4 * warm + pulse * 1.4, 0, Math.PI * 2); c.stroke();

        c.fillStyle = "rgba(242,103,58," + (ia * (0.8 + 0.2 * warm)).toFixed(3) + ")";
        dot(c, d.x, d.y, 3 + 1.2 * warm); c.fill();

        c.font = '500 10.5px "IBM Plex Mono", monospace';
        c.textAlign = "left";
        c.fillStyle = "rgba(242,103,58," + (0.66 + 0.28 * warm).toFixed(3) + ")";
        c.fillText("INDIA", d.x + 17, d.y + 21);
        c.lineWidth = 1;
        c.strokeStyle = "rgba(242,103,58," + (0.32 + 0.2 * warm).toFixed(3) + ")";
        c.beginPath(); c.moveTo(d.x + 8, d.y + 9); c.lineTo(d.x + 14, d.y + 16); c.stroke();
      }

      // each arrival opens one quiet ring and fades
      arrivals = arrivals.filter(function (a2) {
        var v = (now - a2.t) / 1500;
        if (v >= 1) return false;
        c.lineWidth = 1.2 * (1 - v);
        c.strokeStyle = "rgba(242,103,58," + (0.5 * (1 - v)).toFixed(3) + ")";
        c.beginPath(); c.arc(d.x, d.y, 9 + v * 30, 0, Math.PI * 2); c.stroke();
        return true;
      });

      c.restore();
    }

    function init() {
      ctx = cv.getContext("2d");
      if (!ctx) return;
      mtx = 0; mty = 0; px = 0; py = 0;
      t = 0;
      t0 = performance.now();
      arrivals = [];
      landed = 0;
      layoutCanvas();
      loadAtlas();
      sizeUnit();
      start();
    }

    var onMove = function (e) {
      var r = cv.getBoundingClientRect();
      var nx = (e.clientX - r.left) / Math.max(1, r.width);
      var ny = (e.clientY - r.top) / Math.max(1, r.height);
      mtx = Math.max(-1, Math.min(1, nx * 2 - 1));
      mty = Math.max(-1, Math.min(1, ny * 2 - 1));
      cur = { x: nx * (W || r.width), y: ny * (H || r.height) };
    };
    window.addEventListener("pointermove", onMove, { passive: true });

    return {
      init: init,
      resize: function () { layoutCanvas(); sizeUnit(); if (ctx) start(); }
    };
  })();

  /* ============================ building the centre ===================== */
  var build = $("#do-build", root);
  var wideRoot = build && $(".pe-do-wide", build);
  var narrowRoot = build && $(".pe-do-narrow", build);
  var step = 0;

  function branchWide(branch) { return branch.classList.contains("pe-do-wide"); }

  // title, statement and workstreams resolve together, in order
  function revealPanel(p) {
    var bits = $$("[data-in]", p);
    if (!bits.length) return;
    if (window.matchMedia && window.matchMedia("(prefers-reduced-motion: reduce)").matches) {
      bits.forEach(function (el) { el.style.opacity = "1"; el.style.transform = "none"; el.style.transitionDelay = "0ms"; });
      return;
    }
    var show = function () {
      bits.forEach(function (el, i) {
        el.style.transitionDelay = (70 + i * 36) + "ms";
        el.style.opacity = "1";
        el.style.transform = "none";
      });
    };
    if (!p.__revealed) { p.__revealed = true; show(); return; }
    bits.forEach(function (el) { el.style.transitionDelay = "0ms"; el.style.opacity = "0"; el.style.transform = "translateY(9px)"; });
    void p.offsetWidth;
    requestAnimationFrame(show);
    window.setTimeout(show, 24);
  }

  // apply the current step to one branch, using that branch's own width rules
  function applyBranch(branch) {
    if (!branch) return;
    var wide = branchWide(branch);
    var active = step;

    $$("[data-step]", branch).forEach(function (b) {
      var idx = parseInt(b.getAttribute("data-step"), 10) || 0;
      var sel = idx === active;
      b.setAttribute("aria-selected", sel ? "true" : "false");
      b.style.background = sel ? "#FFFFFF" : "transparent";
      b.style.boxShadow = sel ? (wide ? "0 -8px 26px rgba(18,63,68,.05)" : "0 6px 20px rgba(18,63,68,.05)") : "none";
      var bar = $("[data-bar]", b);
      if (bar) bar.style.background = sel ? "#F2673A" : (wide ? "rgba(23,114,111,.16)" : "transparent");
      var edge = $("[data-edge]", b);
      if (edge) edge.style.opacity = sel ? "0" : "1";
      var n = $("[data-num]", b);
      if (n) n.style.color = sel ? "#F2673A" : "#2A868C";
      var nm = $("[data-name]", b);
      if (nm) { nm.style.color = sel ? "#14181A" : "#6B7477"; nm.style.fontWeight = sel ? "600" : "500"; }
    });

    // the shown panel stays in flow so the card grows with its content; the other
    // three are lifted out of flow underneath it. no media queries here: the wide
    // branch is always two-column; the narrow branch collapses below 760px.
    // Narrow branch is an accordion: each panel was moved beneath its own header
    // button (see the build init below), so the active one opens in place and the
    // rest collapse. The wide branch keeps its cross-fading single-panel stack.
    var accordion = !wide && branch.__accordion;
    var stack = wide ? false : (window.innerWidth < 760);
    $$("[data-panel]", branch).forEach(function (p) {
      var on = (parseInt(p.getAttribute("data-panel"), 10) || 0) === active;
      if (accordion) {
        p.style.position = "static";
        p.style.display = on ? "grid" : "none";
        p.style.opacity = on ? "1" : "0";
        p.style.transform = on ? "none" : "translateY(10px)";
        p.style.pointerEvents = on ? "auto" : "none";
        p.setAttribute("aria-hidden", on ? "false" : "true");
        p.style.gridTemplateColumns = "minmax(0,1fr)";
        p.style.gap = "22px";
        p.style.padding = "24px 20px 28px";
        p.style.background = "#FFFFFF";
        p.style.boxShadow = on ? "0 14px 38px rgba(18,63,68,.06)" : "none";
        var ha = $("h3", p);
        if (ha) { ha.style.fontSize = "22px"; ha.style.maxWidth = "none"; }
        if (on) revealPanel(p);
        return;
      }
      p.style.position = on ? "relative" : "absolute";
      p.style.opacity = on ? "1" : "0";
      p.style.transform = on ? "none" : "translateY(10px)";
      p.style.pointerEvents = on ? "auto" : "none";
      p.setAttribute("aria-hidden", on ? "false" : "true");
      p.style.gridTemplateColumns = stack ? "minmax(0,1fr)" : "minmax(0,.82fr) minmax(0,1.45fr)";
      p.style.gap = stack ? "22px" : "clamp(26px,3.4vw,64px)";
      p.style.padding = stack ? "24px 20px 28px" : "clamp(28px,2.7vw,50px)";
      var h = $("h3", p);
      if (h) { h.style.fontSize = stack ? "22px" : "clamp(22px,1.95vw,33px)"; h.style.maxWidth = stack ? "none" : "16ch"; }
      if (on) revealPanel(p);
    });
  }

  function applySteps() { applyBranch(wideRoot); applyBranch(narrowRoot); }

  function setStep(next) {
    step = Math.max(0, Math.min(3, next));
    applySteps();
  }

  // #do-step-1..4 opens that step, so the homepage can link straight to one
  function stepFromHash() {
    var m = /^#do-step-([1-4])$/.exec(window.location.hash || "");
    return m ? Number(m[1]) - 1 : 0;
  }
  function scrollToSteps() {
    if (!build) return;
    window.setTimeout(function () {
      var top = build.getBoundingClientRect().top + window.scrollY - 96;
      window.scrollTo({ top: Math.max(0, top), behavior: "smooth" });
    }, 120);
  }

  if (build) {
    // Mobile accordion: relocate each stage panel to sit directly under its own
    // header button. The panels ship in one shared block below all four headers,
    // so tapping a header changed content further down the page (you had to
    // scroll to see it). After moving them, hide the now-empty shared block and
    // flag the branch so applyBranch opens the tapped panel in place and
    // collapses the rest.
    if (narrowRoot) {
      var nStage = $("[data-stage]", narrowRoot);
      if (nStage) {
        $$("[data-step]", narrowRoot).forEach(function (b) {
          var idx = parseInt(b.getAttribute("data-step"), 10) || 0;
          var panel = $('[data-panel="' + idx + '"]', nStage);
          if (panel) b.insertAdjacentElement("afterend", panel);
        });
        nStage.style.display = "none";
      }
      narrowRoot.__accordion = true;
    }
    [wideRoot, narrowRoot].forEach(function (branch) {
      if (!branch) return;
      $$("[data-step]", branch).forEach(function (b) {
        b.addEventListener("click", function () { setStep(parseInt(b.getAttribute("data-step"), 10) || 0); });
      });
    });
    step = stepFromHash();
    applySteps();
    if (step) scrollToSteps();
  }

  /* ============================ how we engage =========================== */
  // three ways in: the orange cap draws across, the title takes the teal
  function wayHover(el, on) {
    el.style.transform = on ? "translateY(-3px)" : "none";
    el.style.boxShadow = on
      ? "0 1px 0 rgba(23,114,111,.10),0 18px 44px rgba(18,63,68,.10)"
      : "0 1px 0 rgba(23,114,111,.10),0 10px 30px rgba(18,63,68,.04)";
    var cap = el.querySelector("[data-cap]"); if (cap) cap.style.width = on ? "100%" : "34px";
    var t = el.querySelector("[data-wt]"); if (t) t.style.color = on ? "#17726F" : "#14181A";
    var d = el.querySelector("[data-wd]");
    if (d) {
      d.style.opacity = on ? "1" : ".45";
      d.style.transform = on ? "rotate(45deg) scale(1.35)" : "rotate(45deg) scale(1)";
    }
  }
  var engage = $("#do-engage", root);
  if (engage) {
    $$("[data-way]", engage).forEach(function (card) {
      card.addEventListener("mouseenter", function () { wayHover(card, true); });
      card.addEventListener("mouseleave", function () { wayHover(card, false); });
    });
  }

  /* ============================ leadership drawer ======================== */
  var data = {};
  try { var rawEl = $("#pe-dayone-people"); if (rawEl) data = JSON.parse(rawEl.textContent || "{}"); } catch (e) { data = {}; }
  var lead = $("#do-leadership", root);
  var drawer = $("[data-drawer]", root), veil = $("[data-veil]", root);
  var drX = $("[data-dr-x]", root), drKind = $("[data-dr-kind]", root), drScroll = $("[data-dr-scroll]", root);
  var drImg = $("[data-dr-img]", root), drName = $("[data-dr-name]", root), drRole = $("[data-dr-role]", root);
  var drLi = $("[data-dr-li]", root), drBio = $("[data-dr-bio]", root);
  var drState = null, lastFocus = null, settleT = null, hoverKey = null;

  function leadHover(card, on) {
    var q = function (k) { return card.querySelector("[data-" + k + "]"); };
    card.style.background = on ? "#F2673A" : "#FFFFFF";
    var bar = q("bar"); if (bar) bar.style.transform = on ? "none" : "scaleX(0)";
    var shot = q("shot"); if (shot) shot.style.transform = on ? "scale(1.035)" : "none";
    var tag = q("tag"); if (tag) tag.style.color = on ? "rgba(20,24,26,.78)" : "#F2673A";
    var role = q("role"); if (role) role.style.color = on ? "rgba(20,24,26,.74)" : "rgba(8,27,28,.54)";
    var more = q("more"); if (more) more.style.color = on ? "#14181A" : "rgba(8,27,28,.52)";
    var plus = q("plus"); if (plus) plus.style.color = on ? "#14181A" : "#F2673A";
  }
  function applyDrawer(animate) {
    if (!drawer || !veil) return;
    var open = !!drState;
    drawer.style.transition = animate ? "transform .52s cubic-bezier(.4,0,.2,1)" : "none";
    veil.style.transition = animate ? "opacity .4s ease" : "none";
    drawer.style.transform = open ? "none" : "translateX(101%)";
    drawer.style.pointerEvents = open ? "auto" : "none";
    veil.style.opacity = open ? "1" : "0";
    veil.style.pointerEvents = open ? "auto" : "none";
    if (open) drawer.removeAttribute("inert"); else drawer.setAttribute("inert", "");
  }
  function fillDrawer(p) {
    if (drKind) drKind.textContent = p && p.kind ? p.kind : "Profile";
    if (drName) drName.textContent = p ? (p.name || "") : "";
    if (drRole) drRole.textContent = p ? (p.role || "") : "";
    if (drLi) drLi.href = p && p.li ? p.li : "#";
    if (drImg) drImg.style.backgroundImage = p && p.img ? 'url("' + p.img + '")' : "none";
    if (drBio) drBio.innerHTML = p && p.bio ? p.bio : "";
  }
  function settle() {
    window.clearTimeout(settleT);
    settleT = window.setTimeout(function () { applyDrawer(false); }, 580);
  }
  function openProfile(key, trigger) {
    if (!data[key]) return;
    lastFocus = trigger || null;
    drState = key;
    fillDrawer(data[key]);
    applyDrawer(true);
    document.body.style.overflow = "hidden";
    if (drScroll) drScroll.scrollTop = 0;
    if (drX) drX.focus();
    settle();
  }
  function closeProfile() {
    if (!drState) return;
    drState = null;
    applyDrawer(true);
    document.body.style.overflow = "";
    if (lastFocus) lastFocus.focus();
    settle();
  }
  if (lead) {
    $$("[data-p]", lead).forEach(function (card) {
      card.addEventListener("mouseenter", function () { hoverKey = card.dataset.p; leadHover(card, true); });
      card.addEventListener("mouseleave", function () { if (hoverKey === card.dataset.p) hoverKey = null; leadHover(card, false); });
      card.addEventListener("focus", function () { hoverKey = card.dataset.p; leadHover(card, true); });
      card.addEventListener("blur", function () { if (hoverKey === card.dataset.p) hoverKey = null; leadHover(card, false); });
      card.addEventListener("click", function () { openProfile(card.dataset.p, card); });
      card.addEventListener("keydown", function (e) {
        if (e.key === "Enter" || e.key === " ") { e.preventDefault(); openProfile(card.dataset.p, card); }
      });
    });
  }
  if (drX) drX.addEventListener("click", closeProfile);
  if (veil) veil.addEventListener("click", closeProfile);
  window.addEventListener("keydown", function (e) { if (e.key === "Escape" && drState) closeProfile(); });

  /* ============================ clientele marquee ======================== */
  var clients = $("#do-clientele", root);
  var quoteT = null;
  function marqueePause(on) {
    if (!clients) return;
    $$("[data-marquee]", clients).forEach(function (t) { t.style.animationPlayState = on ? "paused" : "running"; });
  }
  // hover a "+" tile: pause the drift and float the testimonial card. No OneNode
  // tile carries a "+", so this only runs if a future edit adds one; kept faithful
  // to the v3 quoteShow/quoteHide so the wiring is complete rather than a dead hook.
  function quoteShow(btn) {
    if (!clients) return;
    var card = $("[data-tcard]", clients);
    if (!card) return;
    window.clearTimeout(quoteT);
    var q = $("[data-tquote]", card); if (q) q.textContent = btn.getAttribute("data-quote") || "";
    var attr = btn.getAttribute("data-attr") || "";
    var a = $("[data-tattr]", card); if (a) { a.textContent = attr; a.style.display = attr ? "block" : "none"; }
    var link = $("[data-tlink]", card); if (link) link.setAttribute("href", btn.getAttribute("data-href") || link.getAttribute("href"));
    marqueePause(true);
    var host = card.parentElement.getBoundingClientRect();
    var br = btn.getBoundingClientRect();
    var cw = card.offsetWidth || 320;
    var ch = card.offsetHeight || 170;
    var left = br.left - host.left - 22;
    left = Math.max(0, Math.min(left, Math.max(0, host.width - cw)));
    card.style.left = left + "px";
    var vh = window.innerHeight || 800;
    var below = br.bottom + 16 + ch <= vh - 12;
    var top = below ? (br.bottom - host.top + 16) : (br.top - host.top - ch - 14);
    card.style.top = top + "px";
    card.style.opacity = "1";
    card.style.transform = "translateY(0)";
    card.style.pointerEvents = "auto";
    card.setAttribute("aria-hidden", "false");
  }
  function quoteHide() {
    if (!clients) return;
    var card = $("[data-tcard]", clients);
    if (!card) return;
    window.clearTimeout(quoteT);
    quoteT = window.setTimeout(function () {
      card.style.opacity = "0";
      card.style.transform = "translateY(6px)";
      card.style.pointerEvents = "none";
      card.setAttribute("aria-hidden", "true");
      marqueePause(false);
    }, 180);
  }
  if (clients) {
    if (reduced) {
      $$("[data-marquee]", clients).forEach(function (t) { t.style.animation = "none"; });
      $$("[data-row]", clients).forEach(function (r) { r.style.overflowX = "auto"; });
    }
    $$("[data-row]", clients).forEach(function (row) {
      row.addEventListener("mouseenter", function () {
        var card = $("[data-tcard]", clients);
        var open = card && card.getAttribute("aria-hidden") === "false";
        var t = row.querySelector("[data-marquee]"); if (t && !open) t.style.animationPlayState = "paused";
      });
      row.addEventListener("mouseleave", function () {
        var card = $("[data-tcard]", clients);
        var open = card && card.getAttribute("aria-hidden") === "false";
        var t = row.querySelector("[data-marquee]"); if (t && !open) t.style.animationPlayState = "running";
      });
    });
    $$(".scp8[data-quote]", clients).forEach(function (btn) {
      btn.addEventListener("mouseenter", function () { quoteShow(btn); });
      btn.addEventListener("mouseleave", quoteHide);
      btn.addEventListener("focus", function () { quoteShow(btn); });
      btn.addEventListener("blur", quoteHide);
    });
  }

  /* ============================ insights card hover ===================== */
  var insights = $("#do-insights", root);
  function cardHover(el, on) {
    var img = el.querySelector("[data-ci]"); if (img) img.style.transform = on ? "scale(1.055)" : "scale(1)";
    var tint = el.querySelector("[data-ct]"); if (tint) tint.style.opacity = on ? "1" : "0";
    var arrow = el.querySelector("[data-ca]"); if (arrow) arrow.style.transform = on ? "translateX(7px)" : "translateX(0)";
  }
  if (insights) {
    $$("a", insights).forEach(function (el) {
      if (!el.querySelector("[data-ci]")) return;
      el.addEventListener("mouseenter", function () { cardHover(el, true); });
      el.addEventListener("mouseleave", function () { cardHover(el, false); });
    });
  }

  /* ============================ closing CTA reveal ====================== */
  var cta = $("#do-begin", root);
  var ctaOn = false;
  function ctaRise() {
    if (ctaOn) return;
    ctaOn = true;
    if (!cta) return;
    $$("[data-rise]", cta).forEach(function (el) { el.style.opacity = "1"; el.style.transform = "none"; });
  }
  if (cta) {
    if (window.matchMedia && window.matchMedia("(prefers-reduced-motion: reduce)").matches) {
      ctaRise();
    } else if ("IntersectionObserver" in window) {
      var obs = new IntersectionObserver(function (entries, o) {
        entries.forEach(function (e) { if (e.isIntersecting) { ctaRise(); o.disconnect(); } });
      }, { threshold: 0.22 });
      obs.observe(cta);
      // an observer callback is not guaranteed (a backgrounded tab never composites),
      // so a cheap scroll/timer check reveals it once it is on screen
      var inView = function () {
        var r = cta.getBoundingClientRect();
        var vh = window.innerHeight || 800;
        return r.top < vh * 0.86 && r.bottom > 0;
      };
      var ctaI = null;
      var check = function () {
        if (ctaOn) { window.clearInterval(ctaI); window.removeEventListener("scroll", check); return; }
        if (inView()) { ctaRise(); window.clearInterval(ctaI); window.removeEventListener("scroll", check); }
      };
      window.addEventListener("scroll", check, { passive: true });
      ctaI = window.setInterval(check, 250);
      window.setTimeout(function () { window.clearInterval(ctaI); ctaRise(); }, 6000);
    } else {
      ctaRise();
    }
  }

  /* ============================ boot + resize =========================== */
  if (Map) Map.init();

  var rzT = null;
  window.addEventListener("resize", function () {
    window.clearTimeout(rzT);
    rzT = window.setTimeout(function () {
      if (Map) Map.resize();
      applySteps();
    }, 120);
  });
})();
